import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Radio, Table, Spin, Alert } from 'antd';
import moment from 'moment-timezone';
import { deviceSensorService } from '../../../services/deviceSensor.service';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import {
    extractPulseEntries,
    calculatePerReadingDeltas,
    aggregateIntoBuckets,
    getIntervalOptions,
    getDoorDateRange,
} from './doorSensorUtils';

const { Title, Text } = Typography;

/**
 * DoorSensorDetail — extracted component for door contact sensor detail page.
 * Handles summary cards, aggregated chart, and interval selection.
 *
 * Props:
 *   sensor        - sensor object with timelines, device_id, id, etc.
 *   selectedTimezone - IANA timezone string
 *   isModal       - whether rendering inside a modal
 *   cardStyle     - style object for card containers
 *   dateFormat    - date format string for API calls
 *   onDateRangeChange - callback(startDate, endDate) to trigger parent data re-fetch
 */
const DoorSensorDetail = ({
    sensor,
    selectedTimezone,
    isModal = false,
    cardStyle = {},
    dateFormat = 'DD/MM/YYYY HH:mm:ss',
    onDateRangeChange,
    timelineData = [],
    timelineColumns = [],
    // Variant props. Defaults give the door-sensor behaviour exactly.
    // The DOOR_CONTACT_SENSOR_WATER variant passes:
    //   displayLabel='Usage', unit='m³',
    //   valueTransform=(pulses)=>pulses*litres_per_pulse/1000, decimals=3,
    //   titleIcon=<WaterDropIcon/>
    displayLabel = 'Counter',
    unit = 'count',
    valueTransform = (n) => n,
    decimals = 0,
    titleIcon = null,
}) => {
    // Single source of truth for the displayed value derived from a pulse
    // count. Applies the transform and caps at `decimals` places of precision,
    // but doesn't pad with trailing zeros — so a zero reading shows as "0"
    // (not "0.000") and 0.180 shows as "0.18". Used everywhere a count would
    // otherwise show.
    const formatValue = (rawCount) => {
        const transformed = valueTransform(Number(rawCount) || 0);
        return String(Number(Number(transformed).toFixed(decimals)));
    };
    const [doorSummary, setDoorSummary] = useState(null);
    const [doorSummaryLoading, setDoorSummaryLoading] = useState(false);
    const [doorSummaryError, setDoorSummaryError] = useState(null);
    const [doorInterval, setDoorInterval] = useState('15min');
    const [doorTimeRange, setDoorTimeRange] = useState('today');
    const initializedRef = useRef(false);

    // Fetch door summary from backend
    useEffect(() => {
        if (sensor?.device_id) {
            setDoorSummaryLoading(true);
            setDoorSummaryError(null);
            deviceSensorService.getDoorSummary(sensor.device_id, { timezone: selectedTimezone })
                .then(res => {
                    if (res && res.summary) {
                        setDoorSummary(res.summary);
                    } else {
                        setDoorSummaryError('Unable to load summary data');
                    }
                })
                .catch(err => {
                    console.error('Error fetching door summary:', err);
                    setDoorSummaryError('Failed to load summary data');
                })
                .finally(() => setDoorSummaryLoading(false));
        }
    }, [sensor?.device_id, selectedTimezone]);

    // Handle time range change from summary card click
    const handleDoorTimeRangeChange = useCallback((range) => {
        setDoorTimeRange(range);
        const { start, end, interval } = getDoorDateRange(range, selectedTimezone);
        setDoorInterval(interval);

        if (onDateRangeChange) {
            onDateRangeChange(start.format(dateFormat), end.format(dateFormat));
        }
    }, [selectedTimezone, dateFormat, onDateRangeChange]);

    // Set initial time range to "today" — guarded to run only once per mount
    useEffect(() => {
        if (selectedTimezone && !initializedRef.current) {
            initializedRef.current = true;
            handleDoorTimeRangeChange('today');
        }
    }, [selectedTimezone, handleDoorTimeRangeChange]);

    // Memoized aggregation of timeline data
    const aggregatedDoorData = useMemo(() => {
        if (!sensor.timelines || !sensor.timelines.length) return [];

        const pulseEntries = extractPulseEntries(sensor.timelines);
        const deltas = calculatePerReadingDeltas(pulseEntries);
        return aggregateIntoBuckets(deltas, doorInterval);
    }, [sensor.timelines, doorInterval]);

    // Constrained interval options based on selected time range
    const intervalOptions = useMemo(() => {
        return getIntervalOptions(doorTimeRange);
    }, [doorTimeRange]);

    // Handle interval change — auto-correct if new range disables current interval
    const handleIntervalChange = useCallback((newInterval) => {
        setDoorInterval(newInterval);
    }, []);

    // When time range changes and current interval becomes disabled, auto-select the default
    useEffect(() => {
        const currentOption = intervalOptions.find(opt => opt.value === doorInterval);
        if (currentOption && currentOption.disabled) {
            const firstEnabled = intervalOptions.find(opt => !opt.disabled);
            if (firstEnabled) {
                setDoorInterval(firstEnabled.value);
            }
        }
    }, [intervalOptions, doorInterval]);

    const renderDoorChart = () => {
        if (!aggregatedDoorData.length) {
            return <Text type="secondary">No data available for the selected time range</Text>;
        }

        const timeFormat = doorInterval === 'daily' ? 'DD/MM' :
            doorInterval === '1hour' ? 'DD/MM HH:mm' : 'HH:mm';
        const parseFormat = doorInterval === 'daily' ? 'DD-MM-YYYY' : 'DD-MM-YYYY HH:mm';

        // Apply the variant value transform to each bucket so the chart Y-axis
        // shows the same units as the summary cards (e.g. m³ for water meter).
        const chartData = aggregatedDoorData.map(d => ({
            ...d,
            value: Number(valueTransform(Number(d.value) || 0).toFixed(decimals)),
            displayTime: moment(d.time, parseFormat).format(timeFormat),
        }));

        return (
            <ResponsiveContainer width="100%" height={isModal ? 300 : 400}>
                <LineChart data={chartData} margin={{ left: 20, right: 10, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="displayTime"
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                    />
                    <YAxis
                        label={{ value: displayLabel, angle: -90, position: 'insideLeft', offset: -5 }}
                        allowDecimals={decimals > 0}
                    />
                    <Tooltip formatter={(value) => [`${value} ${unit}`, displayLabel]} />
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#0B2A1B"
                        strokeWidth={2}
                        dot={{ r: 3, fill: '#0B2A1B' }}
                        activeDot={{ r: 5 }}
                        name={displayLabel}
                    />
                </LineChart>
            </ResponsiveContainer>
        );
    };

    const renderSummaryCardValue = (key) => {
        if (doorSummaryLoading) {
            return <Spin size="small" />;
        }
        if (doorSummaryError) {
            return <Text type="danger" style={{ fontSize: '12px' }}>--</Text>;
        }
        if (!doorSummary || doorSummary[key] == null) return '-';
        // Backend returns raw pulse counts; apply the variant transform here
        // so the summary card displays the same units as the chart.
        return formatValue(doorSummary[key]);
    };

    const summaryCards = [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'daily_average', label: 'Daily Average' },
        { key: 'weekly', label: 'Weekly' },
    ];

    const chartTitle = `${displayLabel} - ${
        doorTimeRange === 'today' ? 'Today' :
        doorTimeRange === 'yesterday' ? 'Yesterday' :
        doorTimeRange === 'weekly' ? 'This Week' :
        'Last 30 Days'
    } (${
        doorInterval === '15min' ? '15 min' :
        doorInterval === '1hour' ? '1 hour' :
        'Daily'
    })`;

    // Chart title with optional leading icon (e.g. water drop for the water variant).
    const chartTitleNode = titleIcon
        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>{titleIcon}{chartTitle}</span>
        : chartTitle;

    return (
        <Row gutter={[isModal ? 16 : 24, isModal ? 16 : 24]}>
            {/* Counter Summary Cards */}
            <Col span={24}>
                <Card title="Summary" style={cardStyle}>
                    {doorSummaryError && (
                        <Alert
                            message={doorSummaryError}
                            type="warning"
                            showIcon
                            style={{ marginBottom: '12px' }}
                        />
                    )}
                    <Row gutter={[isModal ? 12 : 16, isModal ? 12 : 16]}>
                        {summaryCards.map(item => (
                            <Col xs={12} sm={12} lg={6} key={item.key}>
                                <Card
                                    size="small"
                                    hoverable
                                    onClick={() => handleDoorTimeRangeChange(item.key)}
                                    style={{
                                        cursor: 'pointer',
                                        border: doorTimeRange === item.key ? '2px solid #0B2A1B' : undefined,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Text strong style={{ fontSize: isModal ? '12px' : '14px' }}>
                                        {item.label}
                                    </Text>
                                    <div style={{ marginTop: '8px' }}>
                                        <Title
                                            level={isModal ? 4 : 3}
                                            style={{ margin: 0, color: '#0B2A1B' }}
                                        >
                                            {renderSummaryCardValue(item.key)}
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>{unit}</Text>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>
            </Col>

            {/* Interval Selector + Chart (Counter for door, Usage for water) */}
            <Col span={24}>
                <Card
                    title={chartTitleNode}
                    extra={
                        <Radio.Group
                            value={doorInterval}
                            onChange={(e) => handleIntervalChange(e.target.value)}
                            size={isModal ? 'small' : 'middle'}
                        >
                            {intervalOptions.map(opt => (
                                <Radio.Button
                                    key={opt.value}
                                    value={opt.value}
                                    disabled={opt.disabled}
                                >
                                    {opt.label}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    }
                    style={cardStyle}
                >
                    {renderDoorChart()}
                </Card>
            </Col>

            {/* Timeline Table */}
            <Col span={24}>
                <Card title="Recent Data">
                    <Table
                        dataSource={timelineData}
                        columns={timelineColumns}
                        pagination={{
                            pageSize: isModal ? 5 : 10,
                            showSizeChanger: !isModal,
                            showQuickJumper: !isModal,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} readings`
                        }}
                        scroll={{ x: true }}
                        size="small"
                    />
                </Card>
            </Col>
        </Row>
    );
};

export default DoorSensorDetail;
